import { useState, useRef, useEffect, useCallback } from 'react';

const SESSION_KEY = 'qoriax_chat_session';

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function MessageBubble({ msg, botName, botEmoji }) {
  const isBot = msg.role === 'bot';
  return (
    <div className={`flex gap-2 ${isBot ? 'justify-start' : 'justify-end'} animate-fade-in`}>
      {isBot && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--accent-primary)]/20 flex items-center justify-center text-sm">
          {botEmoji}
        </div>
      )}
      <div
        className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
          isBot
            ? 'bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-tl-sm'
            : 'bg-[var(--accent-primary)] text-white rounded-tr-sm'
        }`}
      >
        <p className="whitespace-pre-wrap">{msg.text}</p>
        {msg.buttons && msg.buttons.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {msg.buttons.map((btn) => (
              <button
                key={btn.id}
                onClick={() => btn.onClick?.(btn)}
                className="px-3 py-1.5 text-xs rounded-lg bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/25 transition-colors border border-[var(--accent-primary)]/30"
              >
                {btn.title}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TenantChat({ tenantId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [botInfo, setBotInfo] = useState(null);
  const scrollRef = useRef(null);

  const sessionId = getSessionId();
  const hasTenant = Boolean(tenantId && String(tenantId).trim());

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleOpen = useCallback(async () => {
    if (!hasTenant) return;
    setIsOpen(true);
    if (messages.length === 0) {
      setLoading(true);
      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-tenant-id': tenantId },
          body: JSON.stringify({ action: 'init', session_id: sessionId })
        });
        const data = await res.json();
        if (data.ok) {
          setBotInfo({ name: data.bot_name, emoji: data.bot_emoji });
          setMessages([{ role: 'bot', text: data.message, buttons: data.buttons || [] }]);
        }
      } catch {
        setMessages([{ role: 'bot', text: 'Hola, ¿en qué puedo ayudarte?', buttons: [] }]);
      } finally {
        setLoading(false);
      }
    }
  }, [hasTenant, messages.length, tenantId, sessionId]);

  const sendMessage = useCallback(async (text) => {
    if (!hasTenant || !text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-tenant-id': tenantId },
        body: JSON.stringify({ action: 'message', message: text.trim(), session_id: sessionId })
      });
      const data = await res.json();
      if (data.ok) {
        setMessages((prev) => [
          ...prev,
          { role: 'bot', text: data.message, buttons: data.buttons || [] }
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: 'Disculpa, tuve un problema. ¿Podrías repetir?', buttons: [] }
      ]);
    } finally {
      setLoading(false);
    }
  }, [hasTenant, loading, tenantId, sessionId]);

  const handleButton = useCallback((btn) => {
    sendMessage(btn.title);
  }, [sendMessage]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    sendMessage(input);
  }, [input, sendMessage]);

  if (!hasTenant) return null;

  return (
    <>
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[var(--accent-primary)] text-white shadow-lg hover:scale-110 transition-transform flex items-center justify-center text-2xl"
          aria-label="Abrir chat"
        >
          {botInfo?.emoji || '💬'}
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] max-w-[400px] h-[500px] max-h-[80vh] bg-[var(--bg-primary)] rounded-2xl shadow-2xl border border-[var(--bg-tertiary)] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-[var(--bg-secondary)] border-b border-[var(--bg-tertiary)]">
            <div className="flex items-center gap-2">
              <span className="text-xl">{botInfo?.emoji || '💬'}</span>
              <div>
                <p className="text-sm font-semibold text-[var(--text-primary)]">
                  {botInfo?.name || 'Asistente'}
                </p>
                <p className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  En línea
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors text-xl"
              aria-label="Cerrar chat"
            >
              ✕
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading && messages.length === 0 && (
              <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                <span className="animate-pulse">●</span>
                <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>●</span>
                <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>●</span>
              </div>
            )}
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                msg={{
                  ...msg,
                  buttons: msg.buttons?.map((btn) => ({ ...btn, onClick: handleButton }))
                }}
                botName={botInfo?.name}
                botEmoji={botInfo?.emoji}
              />
            ))}
            {loading && messages.length > 0 && (
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded-full bg-[var(--accent-primary)]/20 flex items-center justify-center text-sm">
                  {botInfo?.emoji || '💬'}
                </div>
                <div className="px-4 py-2.5 rounded-2xl bg-[var(--bg-secondary)] rounded-tl-sm">
                  <span className="flex gap-1">
                    <span className="animate-pulse text-xs">●</span>
                    <span className="animate-pulse text-xs" style={{ animationDelay: '0.2s' }}>●</span>
                    <span className="animate-pulse text-xs" style={{ animationDelay: '0.4s' }}>●</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-3 border-t border-[var(--bg-tertiary)] flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] text-[var(--text-primary)] text-sm placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-4 py-2.5 rounded-xl bg-[var(--accent-primary)] text-white text-sm font-medium disabled:opacity-50 hover:bg-[var(--accent-primary)]/90 transition-colors"
            >
              ➤
            </button>
          </form>
        </div>
      )}
    </>
  );
}
